// ============================================================
// TMR Portal Admin — importar toda a base de produtores do legado (19)
// ============================================================
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, StatusBadge } from '../../components/primitives';
import { PageHeader, Table, Td } from '../../components/ui';
import { Notice } from '../../components/Notice';
import { EmptyState } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useBackfillLegacyCompanies } from '../../data/companies/queries';
import { gapSummary } from '../../data/companies/gaps';
import type { BackfillBatch, BackfillResult } from '../../data/companies/contract';

/** What the walk has done so far, across every batch of one run. */
interface BackfillRun {
  imported: number;
  incomplete: number;
  alreadyPresent: number;
  /** Empresas already here whose empty segment the walk filled from the legacy's products (32 §12). */
  segmentsFilled: number;
  failed: number;
  batches: number;
  /** Only the ones worth reading afterwards: what failed, and what arrived incomplete. */
  attention: BackfillResult[];
  /** False while it is still going, or when the operator stopped it before the end. */
  completed: boolean;
}

const EMPTY_RUN: BackfillRun = {
  imported: 0,
  incomplete: 0,
  alreadyPresent: 0,
  segmentsFilled: 0,
  failed: 0,
  batches: 0,
  attention: [],
  completed: false,
};

/** Adds one batch into the running total. */
function accumulate(run: BackfillRun, batch: BackfillBatch): BackfillRun {
  return {
    imported: run.imported + batch.imported,
    incomplete: run.incomplete + batch.incomplete,
    alreadyPresent: run.alreadyPresent + batch.alreadyPresent,
    segmentsFilled: run.segmentsFilled + batch.segmentsFilled,
    failed: run.failed + batch.failed,
    batches: run.batches + 1,
    // Everything that came across whole is a row nobody needs to read. Keeping only what needs a person
    // is also what stops the report from becoming a thousand lines nobody scrolls.
    attention: [
      ...run.attention,
      ...batch.results.filter((r) => r.outcome === 'Failed' || r.gaps.length > 0),
    ],
    completed: batch.lastLegacyProducerId === null,
  };
}

/**
 * Brings the legacy's whole producer base into the portal (19 §3).
 *
 * It exists because the Empresas list was only ever going to show the ones registered in the new portal —
 * a small and arbitrary slice of the real portfolio — and because assigning a Gestor de Venda or de
 * Pós-venda (18) across a base that is mostly not here is not something anybody can do.
 *
 * **The walk lives here rather than on the server**, exactly like the rate sync it is modelled on: each
 * batch is several legacy round trips per producer, and a request that did all of them would be one the
 * operator could neither watch nor stop.
 *
 * The thing to understand before pressing it: **a producer the legacy never made complete comes across
 * anyway**, carrying what it is missing. Those Empresas are in the portal, filterable, and cannot be
 * replicated back to the legacy until somebody fills the gaps in. That is deliberate — leaving them out
 * would have meant a portfolio that is half here, and half here is not a portfolio.
 */
export function LegacyBackfillPage() {
  const navigate = useNavigate();
  const canBackfill = usePermission(PERMISSIONS.companyImportAdmin);

  const [run, setRun] = useState<BackfillRun | null>(null);
  const [walking, setWalking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Where to resume after a stop or a failure, so neither costs the work already done. */
  const [resumeFrom, setResumeFrom] = useState<number | null>(null);

  // A ref, not state: the loop reads it between batches, and a state value captured in an async closure
  // would still be the one from when the walk started — the button would change its label and nothing else.
  const stopRequested = useRef(false);
  const [stopping, setStopping] = useState(false);

  const backfill = useBackfillLegacyCompanies();

  /**
   * Walks the legacy base one batch at a time, carrying the cursor, until it ends or the operator stops.
   *
   * @param from The cursor to start from — null for the beginning, or the last good one to resume.
   */
  const walk = async (from: number | null) => {
    setWalking(true);
    setError(null);
    stopRequested.current = false;
    setStopping(false);

    let total = run && from !== null ? run : EMPTY_RUN;
    let after = from;

    try {
      for (;;) {
        const batch = await backfill.mutateAsync(after);
        total = accumulate(total, batch);
        setRun(total);

        // Null cursor is the end of the base — the only reliable signal, since a batch can legitimately
        // import nothing and still have more to read.
        if (batch.lastLegacyProducerId === null) {
          setResumeFrom(null);
          break;
        }

        after = batch.lastLegacyProducerId;
        setResumeFrom(after);

        if (stopRequested.current) {
          setRun({ ...total, completed: false });
          break;
        }
      }
    } catch (cause) {
      // The cursor survives the failure, so "Continuar de onde parou" really does — restarting from zero
      // would be minutes of work thrown away over one bad request.
      setError(cause instanceof Error ? cause.message : 'Não foi possível continuar a importação.');
    } finally {
      setWalking(false);
      setStopping(false);
    }
  };

  if (!canBackfill) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <PageHeader eyebrow="Carteira" title="Importar do legado" desc={DESCRIPTION} />
        <EmptyState
          message="Você não tem permissão para importar a base do legado."
          hint="Peça o acesso a quem administra os papéis. Importar um produtor específico é outra permissão."
        />
      </div>
    );
  }

  const stats = run
    ? [
        { label: 'Importadas', value: run.imported },
        { label: 'Incompletas', value: run.incomplete },
        { label: 'Já estavam aqui', value: run.alreadyPresent },
        { label: 'Segmentos preenchidos', value: run.segmentsFilled },
        { label: 'Falharam', value: run.failed },
      ]
    : [];

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Carteira"
        title="Importar do legado"
        desc={DESCRIPTION}
        actions={
          walking ? (
            <Btn
              variant="secondary"
              onClick={() => {
                stopRequested.current = true;
                setStopping(true);
              }}
              disabled={stopping}
            >
              {stopping ? 'Parando…' : 'Parar'}
            </Btn>
          ) : (
            <Btn
              variant="primary"
              icon={Icons.download}
              onClick={() => void walk(resumeFrom)}
            >
              {resumeFrom === null && run === null
                ? 'Importar tudo'
                : resumeFrom === null
                  ? 'Importar de novo'
                  : 'Continuar de onde parou'}
            </Btn>
          )
        }
      />

      {run === null && !walking && (
        <Notice tone="neutral">
          A importação traz <strong>todas</strong> as empresas do sistema legado, em lotes, e pode ser
          interrompida e retomada a qualquer momento — importar de novo o que já veio não muda nada.
          A única coisa que ela faz com uma empresa que já está aqui é preencher o <strong>segmento</strong>,
          quando ele está vazio e todos os produtos dela no legado são do mesmo segmento.
          Empresas cujo cadastro no legado está incompleto <strong>entram assim mesmo</strong>, sinalizadas:
          elas aparecem na lista e ficam bloqueadas para replicação até alguém completar o que falta.
        </Notice>
      )}

      {run !== null && (
        <div className="pa-kpi-grid">
          {stats.map((s) => (
            <div key={s.label} className="pa-card" style={{ padding: 18 }}>
              <div className="pa-kpi-label" style={{ marginBottom: 8 }}>{s.label}</div>
              <div className="pa-num" style={{ fontSize: 26, lineHeight: 1 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {walking && (
        <Notice tone="neutral">
          Importando… {run?.batches ?? 0} {run?.batches === 1 ? 'lote processado' : 'lotes processados'}.
          Pode parar entre lotes — o que já veio fica.
        </Notice>
      )}

      {error && (
        <Notice tone="danger">
          {error} Nada do que já foi importado se perdeu — use “Continuar de onde parou”.
        </Notice>
      )}

      {run !== null && !walking && !error && (
        <Notice tone={run.failed > 0 ? 'warning' : 'success'}>
          {run.completed
            ? `Importação concluída: ${run.imported} empresa(s) trazida(s) do legado.`
            : `Importação interrompida: ${run.imported} empresa(s) trazida(s) até aqui.`}
          {run.incomplete > 0 && (
            <> {run.incomplete} veio com cadastro incompleto e precisa ser completada antes de replicar.</>
          )}
        </Notice>
      )}

      {run !== null && run.attention.length > 0 && (
        <>
          <div className="pa-eyebrow">O que precisa de atenção</div>
          <Table
            columns={[
              { label: 'Produtor no legado' },
              { label: 'Empresa' },
              { label: 'Situação' },
              { label: 'O que falta' },
            ]}
          >
            {run.attention.map((result) => (
              <tr
                key={result.legacyProducerId}
                className="pa-row"
                onClick={result.companyId ? () => navigate(`/empresas/${result.companyId}`) : undefined}
                style={{ cursor: result.companyId ? 'pointer' : 'default' }}
              >
                <Td>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                    {result.legacyProducerId}
                  </span>
                </Td>
                <Td>{result.name || '—'}</Td>
                <Td>
                  <StatusBadge status={result.outcome === 'Failed' ? 'Failed' : 'Pending'} />
                </Td>
                <Td>
                  <span style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                    {/* A failure needs its reason; an incomplete import needs the list of fields. They are
                        different problems and the column says which one this row is. */}
                    {result.error ?? gapSummary(result.gaps)}
                  </span>
                </Td>
              </tr>
            ))}
          </Table>
        </>
      )}

      {run !== null && run.attention.length === 0 && !walking && (
        <div className="pa-card">
          <EmptyState
            message="Nenhuma empresa precisou de atenção."
            hint="Todos os produtores importados vieram com o cadastro completo."
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <StrokeIcon d={Icons.chevronR} size={14} />
        <button type="button" className="pa-link-btn" onClick={() => navigate('/empresas')}>
          Ver a lista de empresas
        </button>
      </div>
    </div>
  );
}

const DESCRIPTION =
  'Traz para o TMR todas as empresas que já existem no sistema legado. É pré-requisito para associar '
  + 'gestores e usuários à carteira inteira.';
